import React, { createContext, useContext, useEffect, useState, useCallback } from 'react'
import { User, Session } from '@supabase/supabase-js'
import { supabase } from '../lib/supabase'
import { config } from '../config'
import { usePostHog } from 'posthog-js/react'
import {
  IMPERSONATION_STATE_KEY,
  IMPERSONATION_STORAGE_KEY,
  readImpersonationId,
  readImpersonationState,
  type ImpersonationState,
} from '../utils/impersonation'

interface AuthContextType {
  user: User | null
  session: Session | null
  loading: boolean
  notInvited: { show: boolean; email: string } | null
  signInWithGoogle: () => Promise<void>
  signOut: () => Promise<void>
  retryAuth: () => Promise<void>
  attemptSessionRecovery: () => Promise<boolean>
  impersonatedUserId: string | null
  impersonatedUserEmail: string | null
  isImpersonating: boolean
  effectiveUserId: string | null
  effectiveUserEmail: string | null
  refreshImpersonation: () => void
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

interface AuthProviderProps {
  children: React.ReactNode
}

// Request deduplication for invite checks
const pendingInviteChecks = new Map<string, Promise<boolean>>()
const INVITE_CACHE_TTL = 60 * 60 * 1000 // 1 hour
const INVITE_CACHE_KEY = 'integuru_invite_cache'

// Helper function to check if user is invited via backend
const checkIfUserInvited = async (email: string): Promise<boolean> => {
  // Check localStorage cache first
  try {
    const cached = localStorage.getItem(INVITE_CACHE_KEY)
    if (cached) {
      const { email: cachedEmail, allowed, timestamp } = JSON.parse(cached)
      if (cachedEmail === email && Date.now() - timestamp < INVITE_CACHE_TTL) {
        return allowed
      }
      // Cache expired, remove it
      localStorage.removeItem(INVITE_CACHE_KEY)
    }
  } catch (e) {
    // Invalid cache data, remove it
    localStorage.removeItem(INVITE_CACHE_KEY)
    
    return true
  }
  
  // Check if there's already a pending request for this email
  if (pendingInviteChecks.has(email)) {
    return pendingInviteChecks.get(email)!
  }
  
  // Create new request
  const checkPromise = (async () => {
    try {
      const controller = new AbortController()
      const timeoutId = setTimeout(() => controller.abort(), 10000) // 10 second timeout
      
      const response = await fetch(config.getApiUrl('/auth/check-invite'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
        signal: controller.signal
      })
      
      clearTimeout(timeoutId)
      
      if (response.ok) {
        const data = await response.json()
        const allowed = data.allowed
        // Cache the result in localStorage
        localStorage.setItem(INVITE_CACHE_KEY, JSON.stringify({
          email,
          allowed,
          timestamp: Date.now()
        }))
        return allowed
      }
      
      return false
    } catch (error) {
      console.error('Error checking invite status:', error)
      return false
    } finally {
      // Remove from pending checks
      pendingInviteChecks.delete(email)
    }
  })()
  
  // Store pending request
  pendingInviteChecks.set(email, checkPromise)
  
  return checkPromise
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null)
  const [session, setSession] = useState<Session | null>(null)
  const [loading, setLoading] = useState(true)
  const [notInvited, setNotInvited] = useState<{ show: boolean; email: string } | null>(null)
  const [impersonationState, setImpersonationState] = useState<ImpersonationState | null>(() => readImpersonationState())
  const posthog = usePostHog()

  const refreshImpersonation = useCallback(() => {
    setImpersonationState(readImpersonationState())
  }, [])

  useEffect(() => {
    const handleStorage = (event: StorageEvent) => {
      if (!event.key || [IMPERSONATION_STORAGE_KEY, IMPERSONATION_STATE_KEY].includes(event.key)) {
        refreshImpersonation()
      }
    }

    window.addEventListener('storage', handleStorage)
    const interval = window.setInterval(refreshImpersonation, 1000)

    return () => {
      window.removeEventListener('storage', handleStorage)
      window.clearInterval(interval)
    }
  }, [refreshImpersonation])

  useEffect(() => {
    if (!supabase) {
      setLoading(false)
      return
    }

    // Get initial session
    const getSession = async () => {
      try {
        const { data: { session } } = await supabase!.auth.getSession()
        
        if (session?.user?.email) {
          let isAllowed = true
          
          // Check if user is invited
          try {
            isAllowed = await checkIfUserInvited(session.user.email)
          } catch (e) {
            console.error('Failed to check invite status:', e)
            isAllowed = true
          }
          
          if (isAllowed === false) {
            try {
              await supabase!.auth.signOut()
            } catch (e) {
              console.error('Failed to sign out:', e)
            }
            setNotInvited({ show: true, email: session.user.email })
            setSession(null)
            setUser(null)
            return
          }
          
          // User is allowed
          setSession(session)
          setUser(session.user)
          
          // Identify user in PostHog for session recordings
          if (posthog) {
            posthog.identify(session.user.id, {
              email: session.user.email,
              name: session.user.user_metadata?.full_name || session.user.user_metadata?.name
            })
          }
        } else {
          setSession(session)
          setUser(session?.user ?? null)
        }
      } catch (e) {
        console.error('Supabase getSession failed:', e)
        setSession(null)
        setUser(null)
      } finally {
        setLoading(false)
      }
    }

    getSession()

    // Listen for auth changes - let Supabase handle token refresh automatically
    const { data: { subscription } } = supabase!.auth.onAuthStateChange(
      async (event, session) => {
        console.log('Auth state change:', event, !!session)
        
        if (session?.user?.email && event === 'SIGNED_IN') {
          // Check if user is invited on sign-in
          const isAllowed = await checkIfUserInvited(session.user.email)
          
          if (!isAllowed) {
            // User not allowed - sign them out and show not invited screen
            await supabase!.auth.signOut()
            setNotInvited({ show: true, email: session.user.email })
            return
          }
          
          // User is allowed
          setSession(session)
          setUser(session.user)
          
          // Identify user in PostHog for session recordings
          if (posthog) {
            posthog.identify(session.user.id, {
              email: session.user.email,
              name: session.user.user_metadata?.full_name || session.user.user_metadata?.name
            })
          }
        } else if (session?.user?.email && event === 'TOKEN_REFRESHED') {
          // For token refresh, just update session without checking invites (use cached result)
          setSession(session)
          setUser(session.user)
          
          // Identify user in PostHog for session recordings
          if (posthog) {
            posthog.identify(session.user.id, {
              email: session.user.email,
              name: session.user.user_metadata?.full_name || session.user.user_metadata?.name
            })
          }
        } else if (event === 'SIGNED_OUT') {
          // Reset PostHog identification on logout
          if (posthog) {
            posthog.reset()
          }
          setSession(null)
          setUser(null)
          setNotInvited(null)
        } else {
          setSession(session)
          setUser(session?.user ?? null)
        }
        setLoading(false)
      }
    )

    return () => subscription.unsubscribe()
  }, [])

  // Safety watchdog: if loading remains true for too long, surface UI and provide reset helpers
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (loading) {
        console.warn('[Auth] Loading stuck > 8s. Forcing UI to render and exposing reset helpers (window.integuruResetAuth)')
        setLoading(false)
      }
    }, 8000)

    return () => clearTimeout(timeoutId)
  }, [loading])

  // Developer helpers to diagnose and reset potentially corrupted auth/session storage
  useEffect(() => {
    ;(window as any).integuruDebugAuth = async () => {
      try {
        const lsKeys = Object.keys(localStorage)
        const ssKeys = Object.keys(sessionStorage)
        console.log('[Auth][Debug] localStorage keys:', lsKeys)
        console.log('[Auth][Debug] sessionStorage keys:', ssKeys)
        if (supabase) {
          const { data: { session: current } } = await supabase.auth.getSession()
          console.log('[Auth][Debug] Supabase session present:', !!current)
          if (current) {
            console.log('[Auth][Debug] Session user email:', current.user?.email)
            console.log('[Auth][Debug] Access token length:', current.access_token?.length)
          }
        } else {
          console.warn('[Auth][Debug] Supabase not configured')
        }
      } catch (e) {
        console.error('[Auth][Debug] Error inspecting auth state:', e)
      }
    }

    ;(window as any).integuruResetAuth = async () => {
      try {
        if (supabase) {
          try {
            await supabase.auth.signOut()
          } catch (e) {
            console.warn('[Auth][Reset] signOut failed, proceeding to clear storage anyway:', e)
          }
        }
        // Preserve only tutorial flag
        const tutVal = localStorage.getItem('tutorial_seen_v1')
        localStorage.clear()
        if (tutVal) localStorage.setItem('tutorial_seen_v1', tutVal)
        sessionStorage.clear()
        console.log('[Auth][Reset] Cleared storage. Reloading...')
        window.location.reload()
      } catch (e) {
        console.error('[Auth][Reset] Error during reset:', e)
      }
    }
  }, [])

  const signInWithGoogle = async () => {
    if (!supabase) {
      throw new Error('Supabase not configured. Please check your environment variables.')
    }
    
    try {
      const redirectUrl = process.env.REACT_APP_ENV === 'prod'
        ? 'https://app.integuru.ai'
        : process.env.REACT_APP_ENV === 'alpha'
          ? 'https://integuru-v1.vercel.app/'
          : 'http://localhost:3000'
      console.log('REACT_APP_ENV:', process.env.REACT_APP_ENV)
      console.log('redirectUrl:', redirectUrl)
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: redirectUrl
        }
      })
      if (error) throw error
    } catch (error) {
      console.error('Error signing in with Google:', error)
      throw error
    }
  }

  const signOut = async () => {
    if (!supabase) {
      // If supabase is not configured, still clear local state
      if (posthog) {
        posthog.reset()
      }
      setSession(null)
      setUser(null)
      setNotInvited(null)
      // Preserve only tutorial flag
      const tutVal = localStorage.getItem('tutorial_seen_v1')
      localStorage.clear()
      if (tutVal) localStorage.setItem('tutorial_seen_v1', tutVal)
      sessionStorage.clear()
      window.location.href = '/'
      return
    }
    
    try {
      // Reset PostHog identification first
      if (posthog) {
        posthog.reset()
      }
      
      // Clear local state first to ensure UI updates
      setSession(null)
      setUser(null)
      setNotInvited(null)
      
      // Then try to sign out from Supabase
      const { error } = await supabase.auth.signOut()
      
      // Even if signOut fails, we've already cleared local state
      if (error) {
        console.error('Supabase signOut error:', error)
        // Don't throw - we still want to clear everything
      }
      
      // Preserve only tutorial flag
      const tutVal2 = localStorage.getItem('tutorial_seen_v1')
      localStorage.clear()
      if (tutVal2) localStorage.setItem('tutorial_seen_v1', tutVal2)
      sessionStorage.clear()
      
      // Force reload to ensure clean state
      window.location.href = '/'
    } catch (error) {
      console.error('Error during signOut:', error)
      // Even on error, clear local state and redirect
      setSession(null)
      setUser(null)
      setNotInvited(null)
      // Preserve only tutorial flag
      const tutVal3 = localStorage.getItem('tutorial_seen_v1')
      localStorage.clear()
      if (tutVal3) localStorage.setItem('tutorial_seen_v1', tutVal3)
      sessionStorage.clear()
      window.location.href = '/'
    }
  }

  const retryAuth = async () => {
    setNotInvited(null)
    await signInWithGoogle()
  }

  const attemptSessionRecovery = async (): Promise<boolean> => {
    try {
      console.log('🔄 Attempting to recover session by refreshing token...')

      if (!supabase) {
        console.error('❌ Supabase not available for session recovery')
        return false
      }

      // Get fresh session from Supabase
      const { data: refreshedSession, error } = await supabase.auth.getSession()

      if (error) {
        console.error('❌ Failed to get refreshed session:', error)
        return false
      }

      if (refreshedSession?.session) {
        console.log('✅ Got refreshed session from Supabase')

        // Try to refresh the token explicitly
        const { data: tokenRefresh, error: refreshError } = await supabase.auth.refreshSession()

        if (refreshError) {
          console.error('❌ Token refresh failed:', refreshError)
          return false
        }

        if (tokenRefresh?.session) {
          console.log('✅ Token refresh successful')
          setSession(tokenRefresh.session)
          setUser(tokenRefresh.session.user ?? null)
          // The AuthContext will automatically update with the new session via onAuthStateChange
          return true
        }
      }

      console.log('❌ No valid session available for recovery')
      return false
    } catch (error) {
      console.error('❌ Session recovery attempt failed:', error)
      return false
    }
  }

  const impersonatedUserId = impersonationState?.userId ?? readImpersonationId()
  const impersonatedUserEmail = impersonationState?.userEmail ?? null
  const isImpersonating = Boolean(impersonatedUserId)

  const effectiveUserId = isImpersonating ? impersonatedUserId : user?.id ?? null
  const effectiveUserEmail = isImpersonating ? (impersonatedUserEmail ?? null) : user?.email ?? null

  const value: AuthContextType = {
    user,
    session,
    loading,
    notInvited,
    signInWithGoogle,
    signOut,
    retryAuth,
    attemptSessionRecovery,
    impersonatedUserId,
    impersonatedUserEmail,
    isImpersonating,
    effectiveUserId,
    effectiveUserEmail,
    refreshImpersonation,
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
} 
